"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface Option<T extends string> {
    value: T;
    label: string;
}

interface Props<T extends string> {
    options: readonly Option<T>[];
    value: T;
    onChange: (next: T, button: HTMLButtonElement) => void;
}

export default function SwitchTabs<T extends string>({ options, value, onChange }: Props<T>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const [pos, setPos] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

    const activeIndex = Math.max(
        0,
        options.findIndex((o) => o.value === value),
    );

    useEffect(() => {
        const update = () => {
            const target = buttonRefs.current[activeIndex];
            if (!target) return;
            setPos({
                left: target.offsetLeft,
                top: target.offsetTop,
                width: target.offsetWidth,
                height: target.offsetHeight,
            });
        };
        update();
        const ro = new ResizeObserver(update);
        if (containerRef.current) ro.observe(containerRef.current);
        for (const btn of buttonRefs.current) {
            if (btn) ro.observe(btn);
        }
        return () => ro.disconnect();
    }, [activeIndex, options.length]);

    return (
        <div
            ref={containerRef}
            className="relative inline-flex items-center gap-1 rounded-full bg-muted/60 p-1 text-xs"
        >
            {pos && (
                <span
                    aria-hidden
                    className="pointer-events-none absolute rounded-full bg-background shadow-sm transition-[left,top,width,height] duration-200 ease-out"
                    style={{ left: pos.left, top: pos.top, width: pos.width, height: pos.height }}
                />
            )}
            {options.map((opt, i) => (
                <button
                    key={opt.value}
                    ref={(el) => {
                        buttonRefs.current[i] = el;
                    }}
                    type="button"
                    onClick={(e) => onChange(opt.value, e.currentTarget)}
                    aria-pressed={value === opt.value}
                    className={cn(
                        "relative z-10 rounded-full px-3 py-1 transition-colors",
                        value === opt.value
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                    )}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}
